/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ModuleSwitcherOverlay — 2-column grid of available modules.
 *
 * Shared by all 3 mobile module switcher styles (tab, header, fab).
 * Each card shows a colored icon circle + module name + subtitle.
 * Active module is highlighted with a color-tinted border and checkmark.
 */

import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import type { ModuleDefinition } from '@/config/module-registry';

interface Props {
  modules: ModuleDefinition[];
  activeModuleId: string | undefined;
  onSelect: (mod: ModuleDefinition) => void;
}

export function ModuleSwitcherOverlay({ modules, activeModuleId, onSelect }: Props) {
  const { t } = useTranslation('common');

  return (
    <div className="bn-modsw-panel">
      <div className="bn-modsw-title">
        {t('moduleSwitcher.switchModule', 'Switch Module')}
      </div>
      <div className="bn-modsw-grid">
        {modules.map((mod) => {
          const Icon = mod.icon;
          const isActive = mod.id === activeModuleId;
          return (
            <button
              key={mod.id}
              className={`bn-modsw-card${isActive ? ' bn-modsw-active' : ''}`}
              style={{
                '--mod-color': mod.color,
              } as React.CSSProperties}
              onClick={() => onSelect(mod)}
              type="button"
            >
              <div className="bn-modsw-icon" style={{ background: `${mod.color}18` }}>
                <Icon size={20} style={{ color: mod.color }} />
              </div>
              <div className="bn-modsw-info">
                <span className="bn-modsw-name">{t(mod.nameKey)}</span>
                <span className="bn-modsw-sub">{t(mod.subtitleKey)}</span>
              </div>
              {isActive && (
                <div className="bn-modsw-check" style={{ background: mod.color }}>
                  <Check size={12} />
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
